import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { FiEdit2 } from "react-icons/fi";
import { MdDeleteForever } from "react-icons/md";
import { useDispatch } from "react-redux";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import { getallCategory } from "../../../../Redux/actions/Operationsactions/getcategory";
import { getallMaterial } from "../../../../Redux/actions/Operationsactions/getmaterial";
import { getRateLibraryRecords, deleteRateLibraryRecord } from "./rateLibraryStorage";

/**
 * Rate Library List Table
 * Styled matching the project standard theme from GatePassTable.jsx
 */
const RateLibrary = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const gridRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [columnVisibility, setColumnVisibility] = useState({});
  const [records, setRecords] = useState([]);

  useEffect(() => {
    dispatch(getallCategory());
    dispatch(getallMaterial());
    setRecords(getRateLibraryRecords());
  }, [dispatch]);

  const handleDelete = useCallback((id) => {
    if (window.confirm("Are you sure you want to delete this Rate Library item?")) {
      const updated = deleteRateLibraryRecord(id);
      setRecords(updated);
    }
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        filter: false,
        sortable: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Activity Name",
        field: "ActivityName",
        flex: 2,
        minWidth: 220,
      },
      {
        headerName: "UOM Base",
        field: "UOMBase",
        flex: 1,
        minWidth: 120,
      },
      {
        headerName: "Category",
        field: "Category",
        flex: 1,
        minWidth: 130,
      },
      {
        headerName: "Unit Estimate Rate",
        field: "UnitEstimateRate",
        flex: 1,
        minWidth: 150,
        valueGetter: (params) => {
          const val = params.data?.UnitEstimateRate;
          return val ? `PKR ${Number(val).toLocaleString()}` : "N/A";
        },
      },
      {
        headerName: "Benchmark Materials",
        flex: 1,
        minWidth: 150,
        valueGetter: (params) => {
          const count = params.data?.MaterialRows?.length || 0;
          return count === 1 ? "1 Material" : `${count} Materials`;
        },
      },
      {
        headerName: "Actions",
        colId: "actions",
        width: 140,
        filter: false,
        sortable: false,
        suppressMenu: true,
        headerComponent: () => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <ActionButton
              text="New+"
              newBtn="true"
              onClick={() => history.push("/works/rate-library/add")}
            />
          </div>
        ),
        cellRenderer: (params) => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              height: "100%",
            }}
          >
            <FiEdit2
              title="Edit"
              size="1.1em"
              style={{ cursor: "pointer", color: "#4ac8d1" }}
              onClick={(e) => {
                e.stopPropagation();
                history.push(`/works/rate-library/edit/${params.data?.Id}`);
              }}
            />
            <MdDeleteForever
              title="Delete"
              size="1.3em"
              style={{ cursor: "pointer", color: "#dc3545" }}
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(params.data?.Id);
              }}
            />
          </div>
        ),
      },
    ],
    [history, handleDelete],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (p) => p.value,
      valueFormatter: (p) =>
        p.value === null || p.value === undefined || p.value === "" ? "N/A" : p.value,
      minWidth: 100,
    }),
    [],
  );

  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({ ...prev, [colId]: !isCurrentlyVisible }));
    },
    [columnVisibility],
  );

  return (
    <>
      <div
        className="d-flex flex-wrap align-items-center justify-content-end pt-3 app_font w-100"
        style={{ gap: "12px", width: "100%" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            minHeight: "30px",
            maxWidth: "100%",
            marginRight: "auto",
            padding: "4px 12px",
            color: "var(--btn-comp-text, #0b6462)",
            fontSize: "var(--font-md)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
          title="Rate Library"
        >
          Rate Library
        </div>
        <SearchFilter
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search Rate Library"
        />
        <ColumnVisibilityControl
          gridApi={gridRef.current?.api}
          columnVisibility={columnVisibility}
          toggleColumn={toggleColumn}
        />
      </div>

      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div className="ag-theme-alpine vouchers_table_height3 mt-3" style={{ width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={records}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              quickFilterText={searchTerm}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export { RateLibrary };
export default RateLibrary;
